import type { DeviceModelRow } from '../../services/hardwareApi';

/**
 * DEVICE RANKING FOR RECEIVING'S SEARCH BOX.
 *
 * Pure and synchronous on purpose. There are ~31 device models in dev and none
 * in prod, and `listModels` already returns all of them unpaginated, so a
 * client-side pass costs nothing and needs no index, no reindex hook and no
 * second source of truth to drift. If the catalogue ever reaches a few thousand
 * models, this one function is the seam to swap for a server call — nothing
 * else in the UI knows how matching works.
 *
 * The tiers matter to a barcode scanner, not just to a human: a scanner types a
 * whole code and presses Enter, so ONLY a whole-barcode match may rank 0. The
 * composer auto-adds a rank-0 hit without showing a picker, and a partial scan
 * that ranked as exact would silently receive stock against the wrong device.
 */

/** Lower is better. -1 means no match. Exported so the caller can spot rank 0. */
export function matchRank(model: DeviceModelRow, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;

  const name = (model.modelName || '').toLowerCase();
  const manufacturer = (model.manufacturer || '').toLowerCase();
  const brand = (model.brand || '').toLowerCase();
  const barcode = (model.sharedBarcode || '').toLowerCase();
  const id = (model.modelId || '').toLowerCase();

  // 0 — the scanner's fast path. Whole barcode only; never a prefix.
  if (barcode && barcode === q) return 0;
  if (id && id === q) return 1;
  if (barcode && barcode.startsWith(q)) return 2;

  if (name.startsWith(q)) return 3;
  // A word anywhere in the name: "printer" must find "Receipt Printer 80mm".
  // Hyphens split too, so "touch" finds "TC-TOUCH-K10".
  if (name.split(/[\s\-/_]+/).some((w) => w.startsWith(q))) return 4;
  if (name.includes(q)) return 5;

  if (manufacturer.startsWith(q) || brand.startsWith(q)) return 6;
  if (manufacturer.includes(q) || brand.includes(q)) return 7;

  return -1;
}

/**
 * Matching models, best first.
 *
 * Rows with no `modelName` are dropped rather than ranked: 9 of the 31 live dev
 * rows have an empty name, and in a picker they render as an unidentifiable
 * blank line that an operator can still click and receive stock against. They
 * are left in the table — cleaning up real data is not this function's call —
 * but they never reach the search box.
 */
export function searchModels(models: DeviceModelRow[], query: string): DeviceModelRow[] {
  const named = models.filter((m) => (m.modelName || '').trim().length > 0);

  const ranked: Array<{ model: DeviceModelRow; rank: number }> = [];
  for (const model of named) {
    const rank = matchRank(model, query);
    if (rank >= 0) ranked.push({ model, rank });
  }

  // Alphabetical inside a tier, so the order cannot wobble between renders and
  // the keyboard selection stays pointed at the row the operator was looking at.
  ranked.sort((a, b) => a.rank - b.rank
    || a.model.modelName.localeCompare(b.model.modelName));

  return ranked.map((r) => r.model);
}
